import { Link, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { Sparkles, ArrowRight, Check, Users, Building2, Briefcase, Brain } from 'lucide-react'
import { useAuth } from '@/stores/authStore'
import { APP_NAME } from '@/config/constants'
import { useTranslation } from 'react-i18next'

type HeroVariant = 'candidate' | 'recruiter'

export default function HomePage() {
  const navigate = useNavigate()
  const { user, isAuthenticated, _hasHydrated } = useAuth()
  const { t } = useTranslation(['home', 'common'])
  const [heroVariant, setHeroVariant] = useState<HeroVariant>('candidate')
  const [isTransitioning, setIsTransitioning] = useState(false)

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true)
      setTimeout(() => {
        setHeroVariant(prev => prev === 'candidate' ? 'recruiter' : 'candidate')
        setIsTransitioning(false)
      }, 300)
    }, 5000)

    return () => clearInterval(interval)
  }, [])

  const handleCandidateClick = (e: React.MouseEvent) => {
    e.preventDefault()
    const hasValidAuth = isAuthenticated && user !== null
    if (!_hasHydrated && !hasValidAuth) return
    if (!isAuthenticated || !user) navigate('/register')
    else if (user.role === 'CANDIDATE') navigate('/candidate/dashboard')
    else if (user.role === 'RECRUITER') navigate('/register')
  }

  const handleRecruiterClick = (e: React.MouseEvent) => {
    e.preventDefault()
    const hasValidAuth = isAuthenticated && user !== null
    if (!_hasHydrated && !hasValidAuth) return
    if (!isAuthenticated || !user) navigate('/register/recruiter')
    else if (user.role === 'RECRUITER') navigate('/recruiter/search')
    else if (user.role === 'CANDIDATE') navigate('/register/recruiter')
  }

  const heroContent = {
    candidate: {
      badge: t('home:hero.candidate.badge'),
      title: t('home:hero.candidate.title'),
      highlight: t('home:hero.candidate.highlight'),
      subtitle: t('home:hero.candidate.subtitle'),
    },
    recruiter: {
      badge: t('home:hero.recruiter.badge'),
      title: t('home:hero.recruiter.title'),
      highlight: t('home:hero.recruiter.highlight'),
      subtitle: t('home:hero.recruiter.subtitle'),
    },
  }

  const currentHero = heroContent[heroVariant]

  const candidateCta = (!_hasHydrated && !(isAuthenticated && user))
    ? t('home:candidate.buttonLoading')
    : isAuthenticated && user?.role === 'CANDIDATE'
      ? t('home:candidate.buttonDashboard')
      : t('home:candidate.buttonCreate')

  const recruiterCta = (!_hasHydrated && !(isAuthenticated && user))
    ? t('home:recruiter.buttonLoading')
    : isAuthenticated && user?.role === 'RECRUITER'
      ? t('home:recruiter.buttonSearch')
      : t('home:recruiter.buttonRecruit')

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-purple-50/60">

      {/* Global decorative glow blobs */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-32 -right-32 w-[32rem] h-[32rem] bg-blue-200 rounded-full blur-3xl opacity-50" />
        <div className="absolute top-1/3 -left-24 w-80 h-80 bg-purple-200 rounded-full blur-3xl opacity-40" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-blue-100 rounded-full blur-3xl opacity-50" />
        <div className="absolute -bottom-24 left-1/3 w-96 h-96 bg-purple-100 rounded-full blur-3xl opacity-40" />
      </div>

      {/* Hero */}
      <section className="relative container-custom pt-20 pb-20 md:pt-28 md:pb-24">
        <div className="text-center max-w-3xl mx-auto">
          {/* Variant indicator dots */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <button
              onClick={() => setHeroVariant('candidate')}
              className={`h-2 rounded-full transition-all duration-300 ${
                heroVariant === 'candidate' ? 'bg-blue-500 w-6' : 'bg-gray-300 hover:bg-gray-400 w-2'
              }`}
              aria-label={t('home:hero.candidate.badge')}
            />
            <button
              onClick={() => setHeroVariant('recruiter')}
              className={`h-2 rounded-full transition-all duration-300 ${
                heroVariant === 'recruiter' ? 'bg-purple-500 w-6' : 'bg-gray-300 hover:bg-gray-400 w-2'
              }`}
              aria-label={t('home:hero.recruiter.badge')}
            />
          </div>

          <div className={`transition-all duration-300 ${isTransitioning ? 'opacity-0 -translate-y-1' : 'opacity-100 translate-y-0'}`}>
            <div className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full mb-6 text-xs font-semibold tracking-wide uppercase border ${
              heroVariant === 'candidate'
                ? 'bg-blue-50 text-blue-600 border-blue-200'
                : 'bg-purple-50 text-purple-600 border-purple-200'
            }`}>
              <Sparkles className="w-3.5 h-3.5" />
              {currentHero.badge}
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-5 text-gray-900 leading-tight tracking-tight">
              {currentHero.title}{' '}
              <span className={`text-transparent bg-clip-text bg-gradient-to-r ${
                heroVariant === 'candidate' ? 'from-blue-600 to-cyan-500' : 'from-purple-600 to-pink-500'
              }`}>
                {currentHero.highlight}
              </span>
            </h1>

            <p className="text-lg text-gray-500 leading-relaxed max-w-2xl mx-auto">
              {currentHero.subtitle}
            </p>

            {/* Hero CTA buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
              <button
                onClick={() => navigate('/register')}
                className={`inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl font-semibold transition-all border ${
                  heroVariant === 'candidate'
                    ? 'bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200 hover:border-blue-300'
                    : 'bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200 hover:border-purple-300'
                }`}
              >
                {t('home:hero.ctaPrimary')}
                <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="#roles"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-600 border border-gray-200 hover:border-gray-300 px-8 py-3.5 rounded-xl font-semibold transition-all"
              >
                {t('home:hero.ctaSecondary')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Dual CTA Cards */}
      <section id="roles" className="relative">
        <div className="border-t border-gray-100" />
        <div className="container-custom py-12 md:py-16">
          <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">

            {/* Candidate Card */}
            <div
              onClick={handleCandidateClick}
              className="group cursor-pointer bg-white/70 border border-gray-200 rounded-2xl hover:bg-white hover:border-gray-300 hover:shadow-lg transition-all hover:-translate-y-1.5 flex flex-col backdrop-blur-sm"
            >
              <div className="p-7 flex flex-col flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
                  {t('home:candidate.title')}
                </h2>
                <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                  {t('home:candidate.subtitle')}
                </p>

                <ul className="space-y-3 mb-8 flex-1">
                  <li className="flex items-center gap-2.5 text-sm text-gray-600">
                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    {t('home:candidate.features.showcase')}
                  </li>
                  <li className="flex items-center gap-2.5 text-sm text-gray-600">
                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    {t('home:candidate.features.highlight')}
                  </li>
                  <li className="flex items-center gap-2.5 text-sm text-gray-600">
                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    {t('home:candidate.features.discovered')}
                  </li>
                </ul>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <span className="text-sm font-bold text-blue-600 group-hover:text-blue-700 transition-colors">
                    {candidateCta}
                  </span>
                  <ArrowRight className="w-4 h-4 text-blue-500 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>

            {/* Recruiter Card */}
            <div
              onClick={handleRecruiterClick}
              className="group cursor-pointer bg-white/70 border border-gray-200 rounded-2xl hover:bg-white hover:border-gray-300 hover:shadow-lg transition-all hover:-translate-y-1.5 flex flex-col backdrop-blur-sm"
            >
              <div className="p-7 flex flex-col flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-1 group-hover:text-purple-600 transition-colors">
                  {t('home:recruiter.title')}
                </h2>
                <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                  {t('home:recruiter.subtitle')}
                </p>

                <ul className="space-y-3 mb-8 flex-1">
                  <li className="flex items-center gap-2.5 text-sm text-gray-600">
                    <Brain className="w-4 h-4 text-purple-500 flex-shrink-0" />
                    {t('home:recruiter.features.ai')}
                  </li>
                  <li className="flex items-center gap-2.5 text-sm text-gray-600">
                    <Check className="w-4 h-4 text-purple-500 flex-shrink-0" />
                    {t('home:recruiter.features.search')}
                  </li>
                  <li className="flex items-center gap-2.5 text-sm text-gray-600">
                    <Check className="w-4 h-4 text-purple-500 flex-shrink-0" />
                    {t('home:recruiter.features.connect')}
                  </li>
                </ul>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <span className="text-sm font-bold text-purple-600 group-hover:text-purple-700 transition-colors">
                    {recruiterCta}
                  </span>
                  <ArrowRight className="w-4 h-4 text-purple-500 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Stats */}
      <section className="relative border-t border-gray-100">
        <div className="container-custom py-12 md:py-16">
          <div className="max-w-3xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <p className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 mb-1">10K+</p>
                <div className="flex items-center justify-center gap-1.5">
                  <Users className="w-4 h-4 text-blue-500" />
                  <p className="text-sm text-gray-500">{t('home:trust.candidates')}</p>
                </div>
              </div>
              <div className="text-center">
                <p className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 mb-1">500+</p>
                <div className="flex items-center justify-center gap-1.5">
                  <Building2 className="w-4 h-4 text-purple-500" />
                  <p className="text-sm text-gray-500">{t('home:trust.companies')}</p>
                </div>
              </div>
              <div className="text-center">
                <p className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 mb-1">5K+</p>
                <div className="flex items-center justify-center gap-1.5">
                  <Briefcase className="w-4 h-4 text-blue-500" />
                  <p className="text-sm text-gray-500">{t('home:trust.hires')}</p>
                </div>
              </div>
              <div className="text-center">
                <p className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 mb-1">50K+</p>
                <div className="flex items-center justify-center gap-1.5">
                  <Brain className="w-4 h-4 text-purple-500" />
                  <p className="text-sm text-gray-500">{t('home:trust.skills')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative border-t border-gray-100">
        <div className="container-custom py-16 md:py-20">
          <div className="max-w-2xl mx-auto text-center">
            <div className="relative overflow-hidden bg-white/70 border border-gray-200 rounded-3xl px-8 py-12 md:px-12 backdrop-blur-sm">
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-12 -right-12 w-48 h-48 bg-blue-100 rounded-full blur-3xl opacity-60" />
                <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-purple-100 rounded-full blur-3xl opacity-60" />
              </div>

              <div className="relative">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 tracking-tight">
                  {t('home:cta.title')}
                </h2>
                <p className="text-gray-500 mb-8">
                  {t('home:cta.subtitle', { appName: APP_NAME })}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link
                    to="/register"
                    className="inline-flex items-center justify-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 hover:border-blue-300 px-7 py-3 rounded-xl font-semibold transition-colors"
                  >
                    {t('home:cta.buttonPrimary')}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/login"
                    className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-600 border border-gray-200 hover:border-gray-300 px-7 py-3 rounded-xl font-semibold transition-colors"
                  >
                    {t('home:cta.buttonSecondary')}
                  </Link>
                </div>
                <p className="text-xs text-gray-400 mt-5">
                  {t('home:cta.benefits')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  )
}
